import * as R from 'ramda'
import { round } from 'lodash-es'
import { removeAbbr } from '@/utils/time'
import { fillDate } from '@/components/patient-stats/common/utils'
import { buildAxis, buildMarkLine } from '@/components/patient-stats/graphs/detailed-reports/options/option-builders'
import moment from 'moment-timezone'
import { CHART_DATE_FORMAT } from '@/components/patient-stats/constants'
import { DataPoint } from '@/jsonapi/CommonTypes'
import YAxis = echarts.EChartOption.YAxis
import XAxis = echarts.EChartOption.XAxis

export function buildSleepEfficiencyOption(options: {
  resultStartTimestamp: number
  resultEndTimestamp: number
  timezone: string
  colors: Record<any, any> // vuetify theme
  sleepEfficiencies: DataPoint[]
}): echarts.EChartOption<echarts.EChartOption.SeriesLine> {
  const { timezone, resultStartTimestamp, resultEndTimestamp, colors, sleepEfficiencies } = options

  const xAxisLabels: string[] = [...fillDate(timezone, resultStartTimestamp, resultEndTimestamp)].map(timestamp =>
    String(timestamp)
  )

  const sleepEfficienciesByDay: { [key: string]: DataPoint[] } = R.groupBy(dataPoint => {
    return moment(dataPoint.timestamp)
      .tz(timezone)
      .startOf('day')
      .valueOf()
      .toString()
  }, sleepEfficiencies)
  const sleepEfficienciesAvgByDay: { [key: string]: number } = Object.entries(sleepEfficienciesByDay).reduce(
    (acc, [key, dpoints]) => {
      acc[key] = round(dpoints.reduce((acc, cur) => acc + cur.value, 0) / dpoints.length)
      return acc
    },
    {}
  )

  const seriesData: Array<[string, number]> = Object.entries(sleepEfficienciesAvgByDay).sort(
    (a, b) => Number(a[0]) - Number(b[0])
  )
  const maxYValue = 100
  const lines = [20, 40, 60, 80]

  return {
    grid: [{ x: '2.5%', y: '5%', width: '95%', height: '95%', containLabel: true } as echarts.EChartOption.Grid],
    xAxis: buildAxis({
      data: xAxisLabels,
      axisType: 'x',
      formatter: param => {
        return moment(parseInt(param, 10))
          .tz(timezone)
          .format(CHART_DATE_FORMAT.DATE)
      },
    }) as XAxis,
    yAxis: buildAxis({
      axisType: 'y',
      maxValue: maxYValue,
    }) as YAxis,
    visualMap: [
      {
        show: false,
        type: 'continuous',
        seriesIndex: 1,
        min: 60,
        max: 80,
        inRange: {
          color: [colors.burgundy, colors.teal],
        },
      },
    ],
    series: [
      {
        type: 'line',
        silent: true,
        stack: 'inhaler usage',
        // @ts-ignore
        markLine: buildMarkLine(lines),
      },
      {
        type: 'line',
        silent: true,
        label: { show: false },
        // @ts-ignore
        data: seriesData.map(d => ({
          value: d,
        })),
      },
    ],
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow',
      },
      formatter: params => {
        const date = moment(parseInt(params[0].axisValue, 10))
          .tz(timezone)
          .format(removeAbbr(timezone, CHART_DATE_FORMAT.DAY_ABBR))
        return `${date} ${params[0].value[1]}%`
      },
    },
  }
}
